import { FormEvent, SetStateAction, useEffect } from "react"
import { toast } from "react-toastify"
import { v4 as uuidv4 } from "uuid"

import { useLazyCheckServiceTitanQuery } from "store/api/contractorsApi"
import { UITypes } from "types"

import { convertDataToOptions } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { contractorSTIntegrationSchema, onFormValidate } from "utils/onValidate"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { Tooltip } from "components/common/Tooltip"
import { InputNew } from "components/form/InputNew"
import { InputTenantID } from "components/form/InputTenantID"
import { SelectStatic } from "components/form/SelectStatic"

import styles from "./index.module.scss"

interface Props {
  inputValues: UITypes.Contractor
  error: {
    field: string
    message: string
  }
  tenantIDStatus: UITypes.TenantIDStautus
  isSTMembershipsFetching: boolean
  contractorMembershipTypes: UITypes.Option[]
  membershipTypesList: UITypes.MembershipType[]
  setError: (value: SetStateAction<{ field: string; message: string }>) => void
  setTenantIDStatus: (value: SetStateAction<UITypes.TenantIDStautus>) => void
  setContractorMembershipTypes: (value: SetStateAction<UITypes.Option[]>) => void
  onInputChange: (event: FormEvent<HTMLInputElement>) => void
}

const IntegrationSection = ({
  inputValues,
  error,
  setError,
  onInputChange,
  tenantIDStatus,
  isSTMembershipsFetching,
  contractorMembershipTypes,
  membershipTypesList,
  setTenantIDStatus,
  setContractorMembershipTypes,
}: Props) => {
  const [triggerCheckServiceTitan, { isFetching: isSTFetching }] = useLazyCheckServiceTitanQuery()

  useEffect(() => {
    if (inputValues.isSTChecked == `1`) {
      setTenantIDStatus("pass")
    }
  }, [])

  const tenantIDHandler = () => {
    if (tenantIDStatus === "error") {
      setTenantIDStatus("check")
    }
    if (tenantIDStatus === "check") {
      onTenantIDCheck()
    }
    if (tenantIDStatus === "pass") {
      setTenantIDStatus("check")
    }
  }

  const onTenantIDCheck = () => {
    if (onFormValidate(inputValues, contractorSTIntegrationSchema, setError, true)) {
      sendTenantIDRequest()
    }
  }

  const sendTenantIDRequest = async () => {
    try {
      const res = await triggerCheckServiceTitan({
        client_id: inputValues.stClientID,
        client_secret: inputValues.stClientSecret,
        app_key: inputValues.stAppKey,
        tenant_id: inputValues.stTenant,
      })
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
        setTenantIDStatus("error")
      } else {
        setTenantIDStatus("pass")
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div>
      <ModalSectionTitle title="Servicetitan Integrations">
        {
          <Tooltip id={uuidv4()} orientation="top" className={styles.titleInformationIcon}>
            Enter the credentials (if available) to enable integration between the Nuve application and ServiceTitan.
          </Tooltip>
        }
      </ModalSectionTitle>
      <div className={styles.row}>
        <InputNew
          label="Client ID"
          placeholder="Enter client ID"
          name="stClientID"
          value={inputValues.stClientID || ""}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "stClientID" ? error.message : ""}
        />
        <InputNew
          label="Client Secret"
          placeholder="Enter client secret"
          name="stClientSecret"
          value={inputValues.stClientSecret || ""}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "stClientSecret" ? error.message : ""}
        />
      </div>
      <div className={styles.row}>
        <InputNew
          label="API Key"
          placeholder="Enter API Key"
          name="stAppKey"
          value={inputValues.stAppKey || ""}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "stAppKey" ? error.message : ""}
        />
        <InputTenantID
          status={isSTFetching ? "loading" : tenantIDStatus}
          label="Tenant ID"
          placeholder="Tenant ID"
          name="stTenant"
          value={inputValues.stTenant || ""}
          onChange={onInputChange}
          buttonHandler={tenantIDHandler}
          errorMessage={error.field === "stTenant" ? error.message : ""}
        />
      </div>
      {tenantIDStatus === "pass" && (
        <div className={styles.row}>
          <SelectStatic
            label="Memberships"
            placeholder="Select Membership"
            multi
            showSelectAllOption
            values={contractorMembershipTypes}
            isLoading={isSTMembershipsFetching}
            options={
              membershipTypesList
                ? convertDataToOptions({ data: [...(membershipTypesList || [])] }, "st_id", "st_name")
                : []
            }
            onChange={setContractorMembershipTypes}
          />
        </div>
      )}
    </div>
  )
}

export default IntegrationSection
